/**
 * Surgeon Sovereignty — Smart Supply Suggestions
 * Mobile-first card interface for one-click preference card updates.
 */

import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  CheckCircle2,
  ArrowRight,
  Clock,
  TrendingUp,
  Sparkles,
  PackageCheck,
  Minus,
  Hash,
  Stethoscope,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { DEMO_SUGGESTIONS, type SupplySuggestion } from '@/lib/supply/suggestionData';
import { toast } from '@/hooks/use-toast';

const PATTERN_CONFIG: Record<SupplySuggestion['patternType'], { label: string; verb: string; color: string }> = {
  never_used: { label: 'Never Used', verb: 'Remove from card', color: 'text-destructive' },
  consistent_substitute: { label: 'Consistent Swap', verb: 'Replace on card', color: 'text-primary' },
  quantity_adjust: { label: 'Qty Adjustment', verb: 'Update quantity', color: 'text-warning' },
};

function PatternIcon({ type }: { type: SupplySuggestion['patternType'] }) {
  if (type === 'never_used') return <Minus className="w-4 h-4 text-destructive" />;
  if (type === 'consistent_substitute') return <ArrowRight className="w-4 h-4 text-primary" />;
  return <Hash className="w-4 h-4 text-warning" />;
}

function SuggestionCard({ suggestion, onSync }: { suggestion: SupplySuggestion; onSync: (id: string) => void }) {
  const [synced, setSynced] = useState(false);
  const config = PATTERN_CONFIG[suggestion.patternType];

  const handleSync = () => {
    setSynced(true);
    onSync(suggestion.id);
  };

  return (
    <Card className={cn(
      'border-border transition-all',
      synced && 'opacity-60 border-success/30',
    )}>
      <CardContent className="p-4 space-y-3">
        {/* Procedure header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Stethoscope className="w-4 h-4 text-primary shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{suggestion.procedureName}</p>
              <p className="text-xs text-muted-foreground">CPT {suggestion.cptCode}</p>
            </div>
          </div>
          <Badge variant="outline" className="text-[10px] shrink-0 border-border">
            {suggestion.caseCount} cases
          </Badge>
        </div>

        <Separator />

        {/* Insight message */}
        <div className="bg-muted/50 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-medium text-primary">Smart Update Suggestion</span>
          </div>

          {suggestion.patternType === 'never_used' && (
            <p className="text-sm leading-relaxed">
              For your last <span className="font-semibold">{suggestion.caseCount}</span> '{suggestion.procedureName}' cases, you{' '}
              <span className="font-semibold text-destructive">never used</span> the{' '}
              <span className="font-medium">{suggestion.currentItem.size} {suggestion.currentItem.name}</span> listed on your card.
            </p>
          )}

          {suggestion.patternType === 'consistent_substitute' && (
            <p className="text-sm leading-relaxed">
              For your last <span className="font-semibold">{suggestion.caseCount}</span> '{suggestion.procedureName}' cases, you never used the{' '}
              <span className="font-medium">{suggestion.currentItem.size} {suggestion.currentItem.name}</span> listed on your card, but consistently used{' '}
              <span className="font-semibold text-primary">{suggestion.suggestedItem.size} {suggestion.suggestedItem.name}</span>.
            </p>
          )}

          {suggestion.patternType === 'quantity_adjust' && (
            <p className="text-sm leading-relaxed">
              For your last <span className="font-semibold">{suggestion.caseCount}</span> '{suggestion.procedureName}' cases, you consistently used{' '}
              <span className="font-semibold text-warning">{suggestion.suggestedItem.quantity}</span> {suggestion.currentItem.name} instead of the{' '}
              <span className="font-medium">{suggestion.currentItem.quantity}</span> on your card.
            </p>
          )}
        </div>

        {/* Change visualization */}
        <div className="flex items-center gap-2 px-1">
          <div className={cn(
            'flex-1 rounded-lg border px-3 py-2 text-center',
            'border-destructive/20 bg-destructive/5',
          )}>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Current Card</p>
            <p className="text-xs font-medium line-through text-muted-foreground">
              {suggestion.currentItem.size ? `${suggestion.currentItem.size} ` : ''}
              {suggestion.currentItem.name} ×{suggestion.currentItem.quantity}
            </p>
          </div>

          <PatternIcon type={suggestion.patternType} />

          <div className={cn(
            'flex-1 rounded-lg border px-3 py-2 text-center',
            'border-success/20 bg-success/5',
          )}>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Suggested</p>
            <p className="text-xs font-medium text-foreground">
              {suggestion.suggestedItem.quantity === 0
                ? 'Remove'
                : `${suggestion.suggestedItem.size ? `${suggestion.suggestedItem.size} ` : ''}${suggestion.suggestedItem.name} ×${suggestion.suggestedItem.quantity}`}
            </p>
          </div>
        </div>

        {/* Value + confidence */}
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                Est. <span className="font-semibold text-foreground">{suggestion.estimatedTimeSavedMinutes}m</span> saved per setup
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <TrendingUp className="w-3 h-3 text-success" />
            <span className="text-[10px] text-success font-medium">{suggestion.confidencePercent}% confidence</span>
          </div>
        </div>

        {/* Action */}
        {synced ? (
          <div className="flex items-center justify-center gap-2 py-2 rounded-lg bg-success/10 border border-success/20">
            <CheckCircle2 className="w-4 h-4 text-success" />
            <span className="text-sm font-medium text-success">Synced to EHR</span>
          </div>
        ) : (
          <Button
            className="w-full gap-2"
            onClick={handleSync}
          >
            <PackageCheck className="w-4 h-4" />
            Sync to Card
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export function SurgeonSupplySuggestions() {
  const [syncedIds, setSyncedIds] = useState<Set<string>>(new Set());
  const surgeonName = 'Dr. John J. Larkin';

  const totalTimeSaved = DEMO_SUGGESTIONS
    .filter(s => syncedIds.has(s.id))
    .reduce((acc, s) => acc + s.estimatedTimeSavedMinutes, 0);

  const handleSync = (id: string) => {
    setSyncedIds(prev => new Set([...prev, id]));
    const suggestion = DEMO_SUGGESTIONS.find(s => s.id === id);
    toast({
      title: 'Preference Card Updated',
      description: `${suggestion?.procedureName} card synced to hospital EHR.`,
    });
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-xl font-bold tracking-tight">Supply Preferences</h1>
        <p className="text-sm text-muted-foreground">
          {surgeonName} · End-of-Day Review
        </p>
      </div>

      {/* Summary strip */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">{DEMO_SUGGESTIONS.length} Smart Updates</span>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Synced</p>
            <p className="text-sm font-bold text-primary">
              {syncedIds.size}/{DEMO_SUGGESTIONS.length}
            </p>
          </div>
        </CardContent>
      </Card>

      {totalTimeSaved > 0 && (
        <Card className="border-success/20 bg-success/5">
          <CardContent className="p-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-success" />
            <span className="text-sm">
              <span className="font-bold text-success">{totalTimeSaved}m</span>
              <span className="text-muted-foreground"> total nursing setup time saved</span>
            </span>
          </CardContent>
        </Card>
      )}

      {/* Suggestion cards */}
      <div className="space-y-4">
        {DEMO_SUGGESTIONS.map(suggestion => (
          <SuggestionCard
            key={suggestion.id}
            suggestion={suggestion}
            onSync={handleSync}
          />
        ))}
      </div>

      {/* Footer */}
      <p className="text-[11px] text-muted-foreground text-center px-4 pt-2">
        All updates require your explicit approval. LINK never modifies your preference card without surgeon authorization.
      </p>
    </div>
  );
}
